import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  applyBps,
  CURRENCIES,
  DOCUMENT_KINDS,
  documentKindLabel,
  formatMoney,
  REGION_CODES,
  REGIONS,
  toMinor,
  UPLOAD_ACCEPT,
  type RegionCode,
} from '@accel/shared';
import { api, ApiError } from '../lib/api';
import { useSession } from '../auth/AuthContext';
import type { DealSummary, Party, Programme } from '../lib/types';
import { Card, ErrorNote, PageHead, Spinner } from '../components/ui';

interface PendingFile {
  file: File;
  kind: string;
}

export function NewDealPage() {
  const session = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [type, setType] = useState<'INVOICE' | 'ORDER'>('INVOICE');
  // A party-bound supplier always originates for itself; Ops and admins must
  // say who they are originating for.
  const [smeId, setSmeId] = useState('');
  const [payerId, setPayerId] = useState('');
  const [programmeId, setProgrammeId] = useState('');
  const [currency, setCurrency] = useState(session.membership.partyName ? 'USD' : 'USD');
  const [amount, setAmount] = useState('');
  const [issueDate, setIssueDate] = useState(today());
  const [dueDate, setDueDate] = useState(addDays(today(), 45));
  const [terms, setTerms] = useState('Net 45');
  const [region, setRegion] = useState<RegionCode>('GLOBAL');
  const [eInvoiceRef, setEInvoiceRef] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const parties = useQuery({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/parties'),
  });

  const programmes = useQuery({
    queryKey: ['programmes'],
    queryFn: () => api.get<Programme[]>('/programmes'),
  });

  const payers = (parties.data ?? []).filter((p) => p.kind === 'PAYER' && p.active);
  const suppliers = (parties.data ?? []).filter((p) => p.kind === 'SME' && p.active);
  const originatesForSelf = Boolean(session.membership.partyId);
  const programme = (programmes.data ?? []).find((p) => p.id === programmeId);

  /**
   * Show the supplier what they will actually receive before they submit.
   * Calculated with the same shared basis-point arithmetic the server uses,
   * so the preview cannot drift from the real posting.
   */
  const preview = useMemo(() => {
    if (!programme || !amount) return null;
    try {
      const faceMinor = toMinor(amount, currency);
      if (faceMinor <= 0n) return null;

      const advanceBps =
        type === 'ORDER' ? (programme.poAdvanceBps ?? programme.advanceBps) : programme.advanceBps;
      const advance = applyBps(faceMinor, advanceBps);
      const fee = applyBps(advance, programme.feeBps);

      return {
        faceMinor,
        advance,
        fee,
        net: advance - fee,
        advanceBps,
        indicative: true,
      };
    } catch {
      // An unparseable amount is a validation message, not a crash.
      return null;
    }
  }, [programme, amount, currency, type]);

  const create = useMutation({
    mutationFn: async () => {
      const faceMinor = toMinor(amount, currency).toString();

      setProgress('Creating the deal…');
      const deal = await api.post<DealSummary>('/deals', {
        type,
        // The server ignores this for a party-bound supplier and uses their
        // own party, so sending it is safe as well as necessary for Ops.
        ...(originatesForSelf ? {} : { smeId }),
        payerId,
        programmeId,
        currency,
        faceMinor,
        issueDate,
        dueDate,
        terms,
        region,
        ...(eInvoiceRef ? { eInvoiceRef, eInvoiceCleared: true } : {}),
        ...(notes ? { notes } : {}),
      });

      // Documents are uploaded after the deal exists, because they attach to
      // it. A failure here leaves a valid draft rather than losing the deal.
      for (const [i, pending] of files.entries()) {
        setProgress(`Uploading ${i + 1} of ${files.length}: ${pending.file.name}`);
        const body = new FormData();
        body.append('file', pending.file);
        body.append('kind', pending.kind);

        try {
          await api.upload(`/deals/${deal.id}/documents`, body);
        } catch (err) {
          // Name the file: "upload failed" on a form with four attachments
          // does not tell the operator which one to fix.
          const message = err instanceof Error ? err.message : 'upload failed';
          throw new ApiError(
            err instanceof ApiError ? err.status : 500,
            `${pending.file.name}: ${message}`,
          );
        }
      }

      setProgress('Submitting for financing…');
      await api.post(`/deals/${deal.id}/transitions`, {
        action: 'SUBMIT',
        reason: 'Submitted by the supplier',
      });

      return deal;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['deals'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/deals');
    },
    onError: (err) => {
      setError(err);
      setProgress(null);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!originatesForSelf && !smeId) {
      return setError(new Error('Choose the supplier you are originating for'));
    }
    if (!payerId) return setError(new Error('Choose the payer this invoice is drawn on'));
    if (!programmeId) return setError(new Error('Choose a financing programme'));
    if (!amount) return setError(new Error('Enter the invoice amount'));
    if (new Date(dueDate) < new Date(issueDate)) {
      return setError(new Error('The due date cannot precede the issue date'));
    }
    create.mutate();
  }

  function addFiles(list: FileList | null) {
    if (!list) return;

    // Read the FileList eagerly. It is live, and the caller clears the input
    // straight after so the same file can be picked twice — a state updater
    // runs later, by which point the list would already be empty.
    const picked = Array.from(list).map((file) => ({
      file,
      kind: type === 'ORDER' ? 'PURCHASE_ORDER' : 'INVOICE',
    }));

    setFiles((current) => [...current, ...picked]);
  }

  if (parties.isLoading || programmes.isLoading) return <Spinner label="Loading" />;

  return (
    <>
      <PageHead
        crumb="Receivables"
        title="Submit a receivable"
        sub="The indicative advance below is calculated with the same arithmetic the ledger uses. Final terms are set when a funder makes an offer."
      />

      <ErrorNote error={error} />

      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 18, alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 18 }}>
            <Card title="What are you financing?">
              <div className="field">
                <label htmlFor="type">Type</label>
                <select id="type" value={type} onChange={(e) => setType(e.target.value as 'INVOICE' | 'ORDER')}>
                  <option value="INVOICE">Invoice — goods or services already delivered</option>
                  <option value="ORDER">Purchase order — not yet delivered</option>
                </select>
              </div>

              {!originatesForSelf ? (
                <div className="field">
                  <label htmlFor="supplier">Supplier</label>
                  <select id="supplier" value={smeId} onChange={(e) => setSmeId(e.target.value)}>
                    <option value="">Choose who is owed the money…</option>
                    {suppliers.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.kycStatus !== 'CLEARED' ? ` (KYC ${p.kycStatus.toLowerCase()})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div className="field">
                <label htmlFor="payer">Payer</label>
                <select id="payer" value={payerId} onChange={(e) => setPayerId(e.target.value)}>
                  <option value="">Choose the buyer who owes the money…</option>
                  {payers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.kycStatus !== 'CLEARED' ? ` (KYC ${p.kycStatus.toLowerCase()})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="programme">Financing programme</label>
                <select
                  id="programme"
                  value={programmeId}
                  onChange={(e) => setProgrammeId(e.target.value)}
                >
                  <option value="">Choose a programme…</option>
                  {(programmes.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {(p.advanceBps / 100).toFixed(0)}% advance,{' '}
                      {(p.feeBps / 100).toFixed(2)}% fee
                      {p.recourse ? '' : ', non-recourse'}
                    </option>
                  ))}
                </select>
              </div>
            </Card>

            <Card title="Amount and dates">
              <div className="grid-2">
                <div className="field">
                  <label htmlFor="currency">Currency</label>
                  <select
                    id="currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                  >
                    {Object.keys(CURRENCIES).map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="field">
                  <label htmlFor="amount">Face value</label>
                  <input
                    id="amount"
                    inputMode="decimal"
                    placeholder="48000.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid-2">
                <div className="field">
                  <label htmlFor="issue">Issue date</label>
                  <input
                    id="issue"
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="due">Due date</label>
                  <input
                    id="due"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid-2">
                <div className="field">
                  <label htmlFor="terms">Payment terms</label>
                  <input id="terms" value={terms} onChange={(e) => setTerms(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="region">Region</label>
                  <select
                    id="region"
                    value={region}
                    onChange={(e) => setRegion(e.target.value as RegionCode)}
                  >
                    {REGION_CODES.map((r) => (
                      <option key={r} value={r}>
                        {REGIONS[r].name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="einv">{REGIONS[region].eInvoiceRefLabel}</label>
                <input
                  id="einv"
                  placeholder="Optional, but strengthens the credit assessment"
                  value={eInvoiceRef}
                  onChange={(e) => setEInvoiceRef(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="notes">Notes</label>
                <input id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </Card>

            <Card title="Supporting documents">
              <p className="cell-t2" style={{ marginTop: 0 }}>
                An invoice with no paperwork behind it is a claim, not a receivable. The credit
                engine expects {type === 'ORDER' ? 'at least the purchase order' : 'at least two documents'}.
                PDF, PNG, JPEG, TIFF, CSV or XLSX, up to 20 MB each.
              </p>

              <input
                type="file"
                multiple
                accept={UPLOAD_ACCEPT}
                aria-label="Add documents"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = '';
                }}
              />

              {files.length > 0 ? (
                <table className="tbl" style={{ marginTop: 14 }}>
                  <tbody>
                    {files.map((f, i) => (
                      <tr key={`${f.file.name}-${i}`} style={{ cursor: 'default' }}>
                        <td>
                          <span className="cell-t1">{f.file.name}</span>
                          <span className="cell-t2" style={{ display: 'block' }}>
                            {(f.file.size / 1024).toFixed(0)} KB
                          </span>
                        </td>
                        <td style={{ width: 190 }}>
                          <select
                            aria-label={`Document type for ${f.file.name}`}
                            className="select"
                            value={f.kind}
                            onChange={(e) =>
                              setFiles((cur) =>
                                cur.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)),
                              )
                            }
                          >
                            {DOCUMENT_KINDS.map((k) => (
                              <option key={k} value={k}>
                                {documentKindLabel(k)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ width: 40 }}>
                          <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </Card>
          </div>

          <div style={{ position: 'sticky', top: 0 }}>
            <Card title="Indicative advance">
              {!preview ? (
                <p className="cell-t2" style={{ margin: 0 }}>
                  Choose a programme and enter an amount to see what you would receive.
                </p>
              ) : (
                <dl className="dl" style={{ gridTemplateColumns: '1fr auto' }}>
                  <dt>Face value</dt>
                  <dd className="num">{formatMoney(preview.faceMinor, currency)}</dd>

                  <dt>Advance at {(preview.advanceBps / 100).toFixed(0)}%</dt>
                  <dd className="num">{formatMoney(preview.advance, currency)}</dd>

                  <dt>Discount fee</dt>
                  <dd className="num">−{formatMoney(preview.fee, currency)}</dd>

                  <dt style={{ fontWeight: 700, color: 'var(--ink)' }}>You receive</dt>
                  <dd className="num" style={{ fontWeight: 700 }}>
                    {formatMoney(preview.net, currency)}
                  </dd>
                </dl>
              )}

              {preview && type === 'ORDER' ? (
                <p className="cell-t2" style={{ marginBottom: 0 }}>
                  Purchase orders advance at a lower rate until delivery is confirmed.
                </p>
              ) : null}

              <div style={{ marginTop: 18, display: 'grid', gap: 8 }}>
                <button className="btn btn-primary" disabled={create.isPending}>
                  {create.isPending ? (progress ?? 'Submitting…') : 'Submit for financing'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => navigate('/deals')}
                  disabled={create.isPending}
                >
                  Cancel
                </button>
              </div>

              <p className="cell-t2" style={{ marginBottom: 0, marginTop: 12 }}>
                Final terms are set when a funder makes an offer, and may differ from this
                estimate after validation.
              </p>
            </Card>
          </div>
        </div>
      </form>
    </>
  );
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  return new Date(new Date(iso).getTime() + days * 86_400_000).toISOString().slice(0, 10);
}
